import {
    LEAD_CONVERSION_TRIGGER_OPTIONS,
    LeadConversionTrigger,
} from './stepConfig.types'
import {
    ConversionTriggerRule,
    TransitionConfig,
} from './transitionConfig.types'
import { PM_MANUAL_TRIGGER } from './transitionTriggerCatalog'

const ALL_CONVERSION_TRIGGERS = LEAD_CONVERSION_TRIGGER_OPTIONS.map(
    (option) => option.value,
)

export function defaultConversionRules(): ConversionTriggerRule[] {
    return ALL_CONVERSION_TRIGGERS.map((trigger) => ({
        trigger,
        enabled: true,
        require_pm_approval: false,
    }))
}

export function enabledTriggersFromRules(
    rules: ConversionTriggerRule[],
): string[] {
    return rules.filter((rule) => rule.enabled).map((rule) => rule.trigger)
}

export function rulesHavePmApproval(rules: ConversionTriggerRule[]): boolean {
    return rules.some(
        (rule) =>
            rule.enabled &&
            rule.require_pm_approval &&
            rule.trigger !== PM_MANUAL_TRIGGER,
    )
}

export function normalizeConversionRules(
    rawRules: ConversionTriggerRule[] | undefined,
    legacyTriggers: string[] | undefined,
    legacyRequirePmApproval = false,
): ConversionTriggerRule[] {
    const allowed = new Set(ALL_CONVERSION_TRIGGERS)
    const enabledSet = new Set(
        legacyTriggers?.filter((trigger) =>
            allowed.has(trigger as LeadConversionTrigger),
        ) ?? ALL_CONVERSION_TRIGGERS,
    )

    if (rawRules?.length) {
        const byTrigger = new Map<string, ConversionTriggerRule>()
        for (const rule of rawRules) {
            if (!allowed.has(rule.trigger as LeadConversionTrigger)) {
                continue
            }
            byTrigger.set(rule.trigger, {
                trigger: rule.trigger,
                enabled: Boolean(rule.enabled),
                require_pm_approval:
                    rule.trigger === PM_MANUAL_TRIGGER
                        ? false
                        : Boolean(rule.require_pm_approval),
            })
        }

        return ALL_CONVERSION_TRIGGERS.map((trigger) => {
            const existing = byTrigger.get(trigger)
            if (existing) {
                return existing
            }
            return {
                trigger,
                enabled: enabledSet.has(trigger),
                require_pm_approval:
                    trigger === PM_MANUAL_TRIGGER
                        ? false
                        : legacyRequirePmApproval && enabledSet.has(trigger),
            }
        })
    }

    return ALL_CONVERSION_TRIGGERS.map((trigger) => ({
        trigger,
        enabled: enabledSet.has(trigger),
        require_pm_approval:
            trigger === PM_MANUAL_TRIGGER
                ? false
                : legacyRequirePmApproval && enabledSet.has(trigger),
    }))
}

export function conversionRulesFromTransitionConfig(
    config: TransitionConfig,
): ConversionTriggerRule[] {
    return normalizeConversionRules(
        config.conversion_rules,
        config.triggers,
        config.require_pm_approval ?? false,
    )
}

export function transitionConfigFromConversionRules(
    rules: ConversionTriggerRule[],
    reviewPrompt?: string,
): TransitionConfig {
    const triggers = enabledTriggersFromRules(rules)
    const config: TransitionConfig = {
        exit_mode: 'conversion',
        triggers,
        conversion_rules: rules.map((rule) => ({
            trigger: rule.trigger,
            enabled: rule.enabled,
            require_pm_approval:
                rule.trigger === PM_MANUAL_TRIGGER
                    ? false
                    : rule.require_pm_approval,
        })),
    }

    if (rulesHavePmApproval(rules)) {
        config.require_pm_approval = true
    }

    if (reviewPrompt?.trim()) {
        config.review_prompt = reviewPrompt.trim()
    }

    return config
}

export function labelForConversionTrigger(trigger: string): string {
    const option = LEAD_CONVERSION_TRIGGER_OPTIONS.find(
        (item) => item.value === trigger,
    )
    return option?.label ?? trigger
}

export function summarizeConversionRules(
    rules: ConversionTriggerRule[],
): string {
    const enabled = rules.filter((rule) => rule.enabled)
    if (enabled.length === 0) {
        return 'No conversion triggers configured'
    }

    const auto = enabled
        .filter(
            (rule) =>
                !rule.require_pm_approval || rule.trigger === PM_MANUAL_TRIGGER,
        )
        .map((rule) => labelForConversionTrigger(rule.trigger))

    const approval = enabled
        .filter(
            (rule) =>
                rule.require_pm_approval && rule.trigger !== PM_MANUAL_TRIGGER,
        )
        .map((rule) => labelForConversionTrigger(rule.trigger))

    const parts: string[] = []
    if (auto.length > 0) {
        parts.push(`Auto: ${auto.join(', ')}`)
    }
    if (approval.length > 0) {
        parts.push(`PM approves: ${approval.join(', ')}`)
    }
    return parts.join(' · ')
}
